import { useMemo, useRef, useState } from 'react'
import {
  CurecallMultiSelect,
  CurecallMultiSelectItem,
  ConfigIcon,
  useClickOutside,
} from '@honestica/curecall-ui'
import { TPlanningFilters } from '../planning.type'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'

interface Props {
  currentFilters: TPlanningFilters
  updatePlanningFilters: (filters: Partial<TPlanningFilters>) => void
  resetFilters: () => void
}

export default function PlanningOtherFilters({
  currentFilters,
  updatePlanningFilters,
  resetFilters,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { availableMedEventOptions, locations = [] } = useCurrentProject()
  const popoverRef = useRef(null)

  const [isOpen, setIsOpen] = useState(false)

  const locationOptions = useMemo<CurecallMultiSelectItem[]>(
    () =>
      locations
        .map((location) => ({ label: location.name, value: location.id }))
        .sort((l1, l2) => l1.label.localeCompare(l2.label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(locations)],
  )

  const selectedMedEvents = useMemo<CurecallMultiSelectItem[]>(
    () => availableMedEventOptions.filter((medEvent) => currentFilters.medEventIds?.includes(medEvent.value)),
    [currentFilters.medEventIds, availableMedEventOptions],
  )

  const selectedLocations = useMemo<CurecallMultiSelectItem[]>(
    () => locationOptions.filter((opt) => currentFilters.locationIds?.includes(opt.value)),
    [currentFilters.locationIds, locationOptions],
  )

  const onChangeMedEventFilter = (medEvents: CurecallMultiSelectItem[]) => {
    updatePlanningFilters({ medEventIds: medEvents.map((medevent) => medevent.value) })
  }

  const onChangeLocationFilter = (selectedOptions: CurecallMultiSelectItem[]) => {
    updatePlanningFilters({ locationIds: selectedOptions.map((opt) => opt.value) })
  }

  const activeFiltersCount = useMemo(() => {
    return (
      (currentFilters?.medEventIds?.length || 0) +
      (locations.length > 1 ? (currentFilters?.locationIds?.length ?? 0) : 0)
    )
  }, [currentFilters.medEventIds, currentFilters.locationIds, locations.length])

  useClickOutside({ ref: popoverRef, callBack: () => setIsOpen(false) })

  return (
    <div className="c-relative c-ml-auto" data-planning-tour="other-filters">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="c-flex c-items-center c-gap-2 c-text-primary c-font-bold hover:c-underline c-mb-2 hover:c-text-primary-500"
        type="button"
      >
        <ConfigIcon className="c-w-4" />
        {t('pages.planning.filters.other-filters', { count: activeFiltersCount })}
      </button>
      {isOpen && (
        <div
          className="c-absolute c-top-full c-right-0 c-bg-grey-50 c-border c-border-grey-500 c-rounded-lg c-shadow-lg c-p-4 c-pr-10 c-z-30 c-min-w-max"
          ref={popoverRef}
        >
          <CurecallMultiSelect
            label={t('pages.planning.filters.med-event')}
            labelClassName="tw-p-0 tw-m-0"
            inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1 tw-w-72"
            wrapperClassName="tw-relative"
            items={availableMedEventOptions}
            selectedItems={selectedMedEvents}
            selectedElementsLabel={t('pages.planning.filters.medevent-selected', {
              number: selectedMedEvents.length,
            })}
            placeholder={t('pages.planning.filters.med-event-placeholder')}
            onSelect={onChangeMedEventFilter}
          />
          {locations.length > 1 && (
            <CurecallMultiSelect
              label={t('pages.planning.filters.location')}
              labelClassName="tw-p-0 tw-m-0 tw-mt-3"
              inputClassName="tw-h-8 [&_li]:c-h-6 [&_li]:c-p-1 tw-w-72"
              wrapperClassName="tw-relative"
              items={locationOptions}
              selectedItems={selectedLocations}
              selectedElementsLabel={t('pages.planning.filters.location-selected', {
                number: selectedLocations.length,
              })}
              placeholder={t('pages.planning.filters.location-placeholder')}
              onSelect={onChangeLocationFilter}
            />
          )}
          <button
            className="c-text-primary c-font-bold hover:c-underline c-ml-1 c-mb-2 c-mt-4"
            onClick={resetFilters}
            type="button"
          >
            {t('pages.planning.filters.reset-filters')}
          </button>
        </div>
      )}
    </div>
  )
}
